import * as React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '../../lib/stores/ui-store';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { GlobalSearch } from '../common/global-search';
import { NotificationsDropdown } from '../common/notifications-dropdown';
import { MOD_SYMBOL } from '../../lib/config/keyboard-shortcuts';
import {
  Search,
  User,
  LogOut,
  Settings,
  HelpCircle,
  Menu,
  Building2,
  UserPlus,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLogout } from '../../lib/api/auth';
import { useUserStore } from '../../lib/stores/user-store';
import { useOrganizationStore } from '../../lib/stores/organization-store';
import { routerPaths } from '../../lib/router-paths';

export function Header() {
  const navigate = useNavigate();
  const { user } = useUserStore()
  const { organization } = useOrganizationStore()
  const { sidebarCollapsed, setSidebarMobileOpen, setOrgSwitcherOpen } = useUIStore();
  const logoutMutation = useLogout()
  const [searchOpen, setSearchOpen] = React.useState(false);

  const userInitials = user
    ? (user.fullName?.[0] || (user as any).name?.[0] || user.email?.[0] || 'U').toUpperCase()
    : 'U';

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 flex h-14 items-center gap-4 border-b bg-background px-4 transition-all duration-300',
        'left-0',
        sidebarCollapsed ? 'min-[860px]:left-16' : 'min-[860px]:left-70'
      )}
    >
      {/* Mobile/Tablet hamburger menu - shows sidebar sheet */}
      <Button
        variant="ghost"
        size="icon"
        className="min-[860px]:hidden"
        onClick={() => setSidebarMobileOpen(true)}
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <div className="flex-1 flex items-center gap-4">
        <Button
          variant="outline"
          className="relative w-full max-w-sm justify-start text-muted-foreground"
          onClick={() => setSearchOpen(true)}
        >
          <Search className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Search loads, drivers, customers...</span>
          <span className="sm:hidden">Search...</span>
          <kbd className="pointer-events-none absolute right-2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">{MOD_SYMBOL}</span>K
          </kbd>
        </Button>
        <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      </div>

      <div className="flex items-center gap-2">
        <NotificationsDropdown />

        <DropdownMenu>
          <DropdownMenuTrigger asChild className='cursor-pointer'>
            <Button variant="ghost" className="flex items-center gap-2 px-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatarUrl} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden min-[860px]:flex flex-col items-start">
                <span className="text-sm font-medium">
                  {user?.fullName || (user as any)?.name || 'User'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {organization?.name}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">
                  {user?.fullName}
                </p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className='cursor-pointer'>
              <Link to="/profile">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              className='cursor-pointer'
              onClick={() => setOrgSwitcherOpen(true)}
            >
              <Building2 className="mr-2 h-4 w-4" />
              Change Organization
            </DropdownMenuItem>
            <DropdownMenuItem asChild className='cursor-pointer'>
              <Link to="/settings">
                <Settings className="mr-2 h-4 w-4" />
                Organization Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className='cursor-pointer'>
              <Link to={routerPaths.myInvitations()}>
                <UserPlus className="mr-2 h-4 w-4" />
                My Invitations
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className='cursor-pointer'>
              <Link to="/help">
                <HelpCircle className="mr-2 h-4 w-4" />
                Help & Support
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                logoutMutation.mutateAsync()
                navigate(routerPaths.login());
              }}
              variant="destructive"
              className='cursor-pointer'
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
